import React from 'react';

import { Divider, Input, Textarea, TextInput, Title } from '@mantine/core';
import { IconFileDescription, IconTags } from '@tabler/icons-react';
import { PhoneInput } from 'react-international-phone';
import { phoneInputCountries } from 'utils/formatters';

import { AthleteInformationField } from './onFileFields';
import { useCheckout } from '../../CheckoutContext';

interface AthleteMedicalInformationProps {
  /** Returning buyer: fields already on file are shown in the summary, not as inputs. */
  lockedFields?: Set<AthleteInformationField>;
}

const AthleteMedicalInformation = ({
  lockedFields = new Set<AthleteInformationField>(),
}: AthleteMedicalInformationProps): JSX.Element => {
  const { form, packageData } = useCheckout();
  const {
    gym: { country },
    packagesalesportalconfiguration: packageSalesPortalConfiguration,
  } = packageData;

  const showEmergencyName =
    packageSalesPortalConfiguration.emergencyNameVisible && !lockedFields.has('emergencyName');
  const showEmergencyPhone =
    packageSalesPortalConfiguration.emergencyPhoneVisible && !lockedFields.has('emergencyPhone');
  const showNotes = packageSalesPortalConfiguration.notesVisible && !lockedFields.has('medicalNotes');

  return (
    <>
      {(showEmergencyName || showEmergencyPhone || showNotes) && (
        <>
          <Divider mx='-xl' my='lg' />
          <Title order={4} weight={500}>
            Medical Information
          </Title>
        </>
      )}

      {showEmergencyName && (
        <TextInput
          data-cy='eContactInput'
          label='Emergency Contact'
          placeholder='Enter Full Name'
          icon={<IconTags />}
          required={packageSalesPortalConfiguration.emergencyNameRequired}
          {...form.getInputProps('athleteInformation.emergencyName')}
        />
      )}

      {showEmergencyPhone && (
        <Input.Wrapper error={form.getInputProps('athleteInformation.emergencyPhone').error}>
          <Input.Label required={packageSalesPortalConfiguration.emergencyPhoneRequired}>
            Emergency Phone Number
          </Input.Label>
          <PhoneInput
            defaultCountry={country.toLowerCase()}
            countries={phoneInputCountries}
            value={form.values.athleteInformation.emergencyPhone || ''}
            onChange={form.setFieldValue.bind(null, 'athleteInformation.emergencyPhone')}
            placeholder='Enter Phone Number'
            style={{ position: 'relative', zIndex: 9999 }}
            required={packageSalesPortalConfiguration.emergencyPhoneRequired}
          />
        </Input.Wrapper>
      )}

      {showNotes && (
        <Textarea
          required={packageSalesPortalConfiguration.notesRequired}
          data-cy='medicalNotesInput'
          label='Medical Notes'
          placeholder='Enter Medical Notes'
          {...form.getInputProps('athleteInformation.medicalNotes')}
          icon={<IconFileDescription />}
        />
      )}
    </>
  );
};

export default AthleteMedicalInformation;
